'use client'

import { cn } from '../../../lib/cn'
import { NoiseSvgFilter } from '../../../lib/noise-filter'

export interface NoiseTextureProps {
  /**
   * Use fixed positioning to cover the entire viewport including navbar.
   * When true, the texture overlays everything at z-[60] (above navbar z-50).
   * Use this when you want consistent texture across the entire page.
   * @default false
   */
  fixed?: boolean
  className?: string
  /** Stable ID for the SVG filter to avoid hydration mismatches in layouts */
  id?: string
}

/**
 * NoiseTexture - Construction paper-like grain overlay
 *
 * A full-page noise texture that adds tactile, organic grain to any background.
 * Uses SVG feTurbulence for procedural noise generation.
 *
 * Fixed settings (not configurable):
 * - Light mode: intensity 0.05, dark specks
 * - Dark mode: intensity 0.10, soft-light blend
 *
 * @example
 * ```tsx
 * // Section-level (default) - covers parent container
 * <NoiseTexture />
 *
 * // Page-level with fixed positioning - covers viewport including navbar
 * <NoiseTexture fixed />
 * ```
 */
export function NoiseTexture({ fixed = false, className, id }: NoiseTextureProps = {}) {
  return (
    <div
      className={cn(
        'pointer-events-none inset-0',
        fixed ? 'fixed z-[60]' : 'absolute z-0',
        className
      )}
    >
      <NoiseSvgFilter id={id} />
    </div>
  )
}
